import { useEffect, useRef, useState } from 'react';
import { Button, IconButton, Tag } from '@teo/components';
import {
  RotateLeft,
  RotateRight,
  Expand,
  Document,
  CrossFilled,
} from '@teo/components/icons';
import { last } from '@/util';

/**
 * ViewUplaodElement – unified magnify / full-screen behaviour for both images and videos.
 *
 * Key points:
 * • Non‑fullscreen overlays are always 100vmin × 100vmin so portrait media never overflows.
 * • Videos now rotate reliably in all browsers (incl. iOS Safari) by applying the CSS transform
 *   **directly** to the <video> element *and* adding the `playsInline` attribute.
 */
export const ViewUplaodElement = ({
  answered,
  el,
  type_txt,
  setUploadFile,
  onDelete,
  uploadFile,
}: any) => {
  /* ----------------------------------------------------
   * local state
   * -------------------------------------------------- */
  const [magnifyImg, setMagnifyImg] = useState(false);
  const [magnifyVid, setMagnifyVid] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);

  const [rotateImg, setRotateImg] = useState(0);
  const [rotateVid, setRotateVid] = useState(0);

  const [fullscreenImg, setFullscreenImg] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    setImageFailed(false);
  }, [el?.url, el?.name, el?.type]);

  /* ----------------------------------------------------
   * helpers
   * -------------------------------------------------- */
  /** normalise image rotation to decide portrait/landscape */
  const normalizedImg = ((rotateImg % 360) + 360) % 360;
  const isVerticalImg = normalizedImg === 90 || normalizedImg === 270;

  /** text tag for type */
  const txt_type = ['doc', 'docx', 'pdf'].includes(type_txt);
  const mimeType = String(el?.type || '').toLowerCase();
  const name = el?.name || last((el?.url || '').split('/'));
  const isImage = mimeType.startsWith('image/');
  const isVideo = mimeType.startsWith('video/');
  const isHeic =
    /\.(heic|heif)$/i.test(name) ||
    /^image\/hei[cf](?:-sequence)?$/.test(mimeType);
  const canPreviewImage = isImage && !isHeic && !imageFailed;
  const typeTag = isImage ? 'Image' : isVideo ? 'Video' : 'Document';

  /* ----------------------------------------------------
   * overlays
   * -------------------------------------------------- */
  const ImageOverlay = () => (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#000000cc]"
      onClick={() => setMagnifyImg(false)}
    >
      <div
        className="pointer-events-none flex h-full w-full items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          alt={name}
          src={el?.url ? el?.url : URL.createObjectURL(el)}
          onError={() => setImageFailed(true)}
          className="pointer-events-auto object-contain transition-transform duration-300"
          style={{
            transform: `rotate(${rotateImg}deg)`,
            maxWidth: fullscreenImg
              ? isVerticalImg
                ? '100vh'
                : '100vw'
              : '100vmin',
            maxHeight: fullscreenImg
              ? isVerticalImg
                ? '100vw'
                : '100vh'
              : '100vmin',
          }}
        />
      </div>

      {/* controls */}
      <OverlayButtons
        rotate={rotateImg}
        setRotate={setRotateImg}
        onToggleFull={() => setFullscreenImg((f) => !f)}
        onClose={() => setMagnifyImg(false)}
      />
    </div>
  );

  const VideoOverlay = () => {
    const normalizedVid = ((rotateVid % 360) + 360) % 360;
    const quarterTurn = normalizedVid === 90 || normalizedVid === 270;

    const videoStyle = {
      maxWidth: quarterTurn ? '100vh' : '100vw',
      maxHeight: quarterTurn ? '100vw' : '100vh',
      transform: `rotate(${rotateVid}deg)`,
      transformOrigin: 'center center',
      objectFit: 'contain' as const,
    };

    return (
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#000000cc]"
        onClick={() => setMagnifyVid(false)}
      >
        <div
          className="flex h-full w-full items-center justify-center"
          onClick={(e) => e.stopPropagation()}
        >
          <video
            ref={videoRef}
            playsInline
            controls
            autoPlay
            style={videoStyle}
            src={el?.url}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          />
        </div>

        <OverlayButtons
          rotate={rotateVid}
          setRotate={setRotateVid}
          onToggleFull={async () => {
            const vid = videoRef.current;
            if (vid?.requestFullscreen) {
              if (!document.fullscreenElement) {
                await vid.requestFullscreen();
              } else {
                await document.exitFullscreen();
              }
            }
          }}
          onClose={() => setMagnifyVid(false)}
        />
      </div>
    );
  };

  /* ----------------------------------------------------
   * helper component: overlay button cluster
   * -------------------------------------------------- */
  const OverlayButtons = ({
    rotate,
    setRotate,
    onToggleFull,
    onClose,
  }: {
    rotate: number;
    setRotate: (fn: (r: number) => number) => void;
    onToggleFull: () => void;
    onClose: () => void;
  }) => (
    <div
      className="fixed top-4 right-4 z-[9999] flex gap-2 rounded bg-[#00000088] p-2"
      onClick={(ev) => ev.stopPropagation()}
    >
      <IconButton
        Icon={Expand}
        className="pointer-events-auto fill-white"
        variant="ghost"
        size="md"
        onClick={(ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          onToggleFull();
        }}
      />
      <IconButton
        Icon={RotateLeft}
        className="pointer-events-auto fill-white"
        variant="ghost"
        size="md"
        onClick={(ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          setRotate((r) => r - 90);
        }}
      />
      <IconButton
        Icon={RotateRight}
        className="pointer-events-auto fill-white"
        variant="ghost"
        size="md"
        onClick={(ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          setRotate((r) => r + 90);
        }}
      />
      <IconButton
        Icon={() => <div className="text-2xl">X</div>}
        className="pointer-events-auto text-white"
        variant="ghost"
        size="md"
        onClick={(ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          onClose();
        }}
      />
    </div>
  );

  /* ----------------------------------------------------
   * render thumbnail / trigger
   * -------------------------------------------------- */
  return (
    <div className="relative flex flex-row items-center rounded border border-grey-02 bg-white p-3 md:max-w-[50%] md:flex-[1_0_49%]">
      {/* ---- image thumbnail ---- */}
      {canPreviewImage && (
        <>
          <Button
            variant="ghost"
            className="mr-3 !p-0"
            onClick={(ev) => {
              ev.preventDefault();
              ev.stopPropagation();
              setRotateImg(0);
              setMagnifyImg(true);
            }}
          >
            <img
              alt={name}
              src={el?.url ? el?.url : URL.createObjectURL(el)}
              onError={() => setImageFailed(true)}
              className="h-16 w-16 rounded border border-tint-indigo object-cover object-center"
            />
          </Button>
          {magnifyImg && <ImageOverlay />}
        </>
      )}

      {/* ---- video thumbnail ---- */}
      {isVideo && (
        <>
          <Button
            variant="ghost"
            className="mr-3 !p-0"
            onClick={(ev) => {
              ev.preventDefault();
              ev.stopPropagation();
              setMagnifyVid(true);
            }}
          >
            <video
              autoPlay
              muted
              playsInline
              className="h-16 w-16 rounded border border-tint-indigo object-cover object-center"
              src={el?.url}
            />
          </Button>
          {magnifyVid && <VideoOverlay />}
        </>
      )}

      {/* ---- file thumbnail, including images this browser cannot display ---- */}
      {!canPreviewImage &&
        !isVideo &&
        (answered ? (
          <a
            href={el?.url}
            target="_blank"
            rel="noreferrer"
            className="mr-3 flex h-16 w-16 rounded border border-error-02 bg-error-01 text-error-05"
          >
            <Document className="m-auto w-10" />
          </a>
        ) : (
          <div className="mr-3 flex h-16 w-16 rounded border border-error-02 bg-error-01 text-error-05">
            <Document className="m-auto w-10" />
          </div>
        ))}

      {/* ---- filename & tag ---- */}
      <div>
        <p className="w-[150px] max-w-[150px] overflow-hidden truncate pb-1 text-base font-semibold">
          {name}
        </p>
        <Tag
          title={typeTag}
          size="md"
          variant="document"
          documentType={txt_type ? 'PDF' : 'image'}
        />
      </div>

      {/* ---- delete during upload ---- */}
      {!answered && (
        <IconButton
          className="my-auto mr-2 ml-auto"
          Icon={CrossFilled}
          variant="outline"
          onClick={() => {
            setUploadFile?.(
              uploadFile?.filter((file: any) => file?.size !== el?.size)
            );
            onDelete && onDelete();
          }}
        />
      )}
    </div>
  );
};
